import { Avatar, Box, Divider, Grid, IconButton, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { useEffect, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog, { DialogSectionTitle } from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import Switch from "../../../../components/Switch";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IDepartment from "../../../../interfaces/IDepartment";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import { RegistrationStatusEnum } from "../../../../data/Enums";
import FormHelper from "../../../../helpers/FormHelper";
import DepartmentService from '../../../../services/DepartmentService';
import useState from "../../../../hooks/useState";
import { useEnvironmentContext } from "../../../../contexts/EnvironmentContext";
import Tooltip from "../../../../components/Tooltip";
import { Add, Delete } from "@mui/icons-material";
import IUser from "../../../../interfaces/IUser";
import { useAuthContext } from "../../../../contexts/AuthContext";
import UserService from "../../../../services/UserService";
import SelectField from "../../../../components/SelectField";


interface IStateValues {
    users: IUser[];

    name: string;
    orderBy: number;
    active: boolean;
    minimumNumberAttachments: number;
    departmentUsers: Partial<IUser>[];
}


const AddEditDialog: React.FunctionComponent<IAddEditDialogProps<IDepartment>> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {}
}) => {
    const { translate } = useLanguageContext();
    const { environment } = useEnvironmentContext();
    const { enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        users: [],

        name: '',
        orderBy: 0,
        active: true,
        minimumNumberAttachments: 0,
        departmentUsers: []
    });


    useEffect(() => {
        if (open) {
            state.reset();

            if (data) {
                state.update('name', data.name);
                state.update('orderBy', data.orderBy);
                state.update('active', Number(data.registrationStatusId) === RegistrationStatusEnum.Active);
                state.update('minimumNumberAttachments', data.minimumNumberAttachments);
                state.update('departmentUsers', data.users);
            }
        }
        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        if (state.values.users.length === 0) {
            let formData = FormHelper.Create(undefined);
            formData = FormHelper.Add(formData, 'enterprise', enterprise?.id);
            formData = FormHelper.Add(formData, 'registrationStatus', RegistrationStatusEnum.Active);

            UserService.List(formData)
                .then(resp => {
                    const users = resp.data.users as IUser[];
                    state.update('users', users);
                });
        }
        // eslint-disable-next-line
    }, [state.values.users]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);
        if (data?.id && data.id !== 0) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        DepartmentService.AddEdit(formData).then(() => {
            handleClose();
            handleListRefresh();
        });
        
        // eslint-disable-next-line
    }, [data]);


    const handleAddDepartmentUser = useCallback(() => {
        const departmentUsersAux = state.values.departmentUsers;

        if (departmentUsersAux.find(u => u.id === 0) === undefined) {
            departmentUsersAux.push({
                id: 0,
            });
    
            state.update('departmentUsers', departmentUsersAux);
        }
        // eslint-disable-next-line
    }, [state.values]);


    const handleChangeDepartmentUser = useCallback((index: number, userId: number) => {
        const departmentUsersAux = state.values.departmentUsers;
        departmentUsersAux[index] = state.values.users.find(u => u.id === userId) ?? {
            id: 0
        };
        
        state.update('departmentUsers', departmentUsersAux);
        // eslint-disable-next-line
    }, [state.values]);


    const handleRemoveDepartmentUser = useCallback((userId: number) => {
        const departmentUsersAux = state.values.departmentUsers.filter(u => u.id !== userId);
        state.update('departmentUsers', departmentUsersAux);
        // eslint-disable-next-line
    }, [state.values]);


    const availableUsersToSelect = useCallback((userId: number) => {
        const selectedUsers = state.values.departmentUsers.filter(u => u.id !== userId).map(u => u.id);
        return state.values.users.filter(u => !selectedUsers.includes(u.id));
        // eslint-disable-next-line
    }, [state.values]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data && data.id !== 0 ? 'edit' : 'add')}
            maxWidth='sm'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={12} xs={12}>
                    <TextField 
                        label={translate('name')} 
                        name='name' 
                        value={state.values.name} 
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 100))}
                    />
                </Grid>

                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('token')}
                        value={data?.token ?? ''} 
                        disabled={true}
                        required={false}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <TextField 
                        type="number"
                        label={translate('ordering')} 
                        name='orderBy' 
                        value={state.values.orderBy} 
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 10))}
                        required={true}
                    />
                </Grid>

                <Grid item xs={12} className='textalign-right'>
                    <Switch
                        label={translate('active')}
                        name='registrationStatusId'
                        value={state.values.active ? RegistrationStatusEnum.Active : RegistrationStatusEnum.Inactive}
                        checked={state.values.active}
                        onChange={e => state.update('active', e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item md={6} xs={8}>
                    <DialogSectionTitle title={translate('users')} />
                </Grid>

                <Grid item md={6} xs={4} className='textalign-right'>
                    <Tooltip title={translate('add')}>
                        <IconButton onClick={handleAddDepartmentUser}>
                            <Add />
                        </IconButton>
                    </Tooltip>
                </Grid>

                <Grid item xs={12} paddingTop={2}>
                    {
                        state.values.departmentUsers.length === 0 ? (
                            <Box className='textalign-center'>
                                <Typography>{translate('noRecordsFound')}</Typography>
                            </Box>
                        ) : (
                            <Table className='borderradius-default backgroundcolor-grey-1'>
                                <TableBody>
                                    {
                                        state.values.departmentUsers.map((user, index) => {
                                            return <TableRow key={index}>
                                                <TableCell className='paddingY-none'>
                                                    <Box className='display-flex gap-20 alignitems-center'>
                                                        <Avatar src={user.avatar} alt={user.fullName} />

                                                        <SelectField 
                                                            name={`departmentUsers[${index}][userId]`} 
                                                            value={user.id}
                                                            onChange={e => handleChangeDepartmentUser(index, e.target.value)}
                                                            margin='none'
                                                            data={[
                                                                { value: 0, text: '-' },
                                                                ...availableUsersToSelect(user.id ?? 0).map(u => ({ value: u.id, text: u.fullName }))
                                                            ]}
                                                            className="backgroundcolor-transparent borderradius-none border-none"
                                                            SelectDisplayProps={{
                                                                style: {
                                                                    paddingTop: 16,
                                                                    paddingRight: 12,
                                                                    paddingBottom: 16,
                                                                    paddingLeft: 12,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                </TableCell>

                                                <TableCell className='textalign-right' width={80}>
                                                    <Tooltip title={translate('remove')}>
                                                        <IconButton 
                                                            className='color-red'
                                                            onClick={() => handleRemoveDepartmentUser(user.id ?? 0)}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        })
                                    }            
                                </TableBody>
                            </Table>
                        )
                    }
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12}>
                    <DialogSectionTitle title={environment.partnerPms.serviceModuleName} />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('minimumNumberAttachments')} 
                        name='minimumNumberAttachments' 
                        value={state.values.minimumNumberAttachments} 
                        type='number'
                        required={false}
                        onChange={e => state.update(e.target.name, ValidationHelper.MaxLength(e.target.value, 2))}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default AddEditDialog;